"use client";

import cn from "@utils/cn";
import classNames from "classnames";
import { RefObject, useEffect, useRef, useState } from "react";
import SwiperCore from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import useMediaQuery from "@/hooks/useMediaQuery";

import Icon from "../ui/Icon/Icon";

type Props = {
  paginationRef: RefObject<any>;
  carouselRef: RefObject<Element>;
  containerRef: RefObject<Element>;
  orderedFirstSlideIndex: {
    title: string;
    year?: number;
    firstIndex: number;
    bulletIcon?: string;
  }[];
  currentYearGroupIndex: number;
  paginationActiveBorderRef: RefObject<HTMLElement>;
  swiperInstance?: SwiperCore;
  setBulletsSwiper?: Function;
  setCurrentBulletIndex: Function;
  bulletType: "Years" | "Icons";
  firstComponent?: boolean;
};

export const HistoryPaginationBullets: React.FC<Props> = ({
  paginationRef,
  orderedFirstSlideIndex,
  currentYearGroupIndex,
  paginationActiveBorderRef,
  swiperInstance,
  setCurrentBulletIndex,
  bulletType,
  carouselRef,
  containerRef,
  firstComponent,
}) => {
  const [swiperBullets, setSwiperBullets] = useState<SwiperCore>();
  const triggerMainSwiperSlide = useRef(false);
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const [scrollOffset, setScrollOffset] = useState(0);

  const handleScroll = () => {
    const container = containerRef.current;
    if (container && isMobile) {
      const containerTop = container.getBoundingClientRect().top;
      if (containerTop <= 0) {
        const offset = Math.min(100, Math.abs(containerTop));
        setScrollOffset(offset);
      } else {
        setScrollOffset(2);
      }
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile, containerRef]);

  useEffect(() => {
    triggerMainSwiperSlide.current = false;
    swiperBullets?.slideTo(currentYearGroupIndex);
    triggerMainSwiperSlide.current = true;
  }, [currentYearGroupIndex]);

  useEffect(() => {
    changeBulletHighlight();
  }, [currentYearGroupIndex]);

  useEffect(() => {
    const swiperInstance = swiperBullets;

    if (swiperInstance) {
      setTimeout(() => {
        onBreakpoint(swiperInstance);
      }, 0);

      swiperInstance.on("breakpoint", onBreakpoint);

      return () => {
        swiperInstance.off("breakpoint", onBreakpoint);
      };
    }
  }, [swiperBullets, paginationActiveBorderRef]);

  function changeBulletHighlight() {
    const bullet = paginationRef.current?.querySelector("#history-bullet-" + currentYearGroupIndex);
    if (currentYearGroupIndex >= 0 && bullet && bulletType === "Years") {
      const currentBullet = bullet as HTMLElement;
      const offsetTop = currentBullet?.offsetTop;
      const bulletBounding = currentBullet?.getBoundingClientRect();
      if (paginationActiveBorderRef.current) {
        const top = offsetTop + bulletBounding.height / 2;
        paginationActiveBorderRef?.current.style.removeProperty("left");
        paginationActiveBorderRef.current.style.top = top + "px";
      }
    }
  }

  function onSwiper(swiper: SwiperCore) {
    setSwiperBullets(swiper);
  }
  function onSlideChange(swiper: SwiperCore) {
    triggerMainSwiperSlide.current && swiperInstance?.slideTo(+(swiper.slides[swiper.activeIndex].getAttribute("data-index") || 0));
  }
  function onBreakpoint(swiper: SwiperCore) {
    if (!swiper.isHorizontal() && paginationActiveBorderRef.current) {
      paginationActiveBorderRef.current.classList.add("moved");
      swiper.wrapperEl.append(paginationActiveBorderRef.current);
    } else if (swiper.isHorizontal() && paginationActiveBorderRef.current && paginationActiveBorderRef.current.classList.contains("moved")) {
      paginationActiveBorderRef.current.style.top = "";
      swiper.el.parentElement?.append(paginationActiveBorderRef.current);
    }
  }
  function handleClick(index: number) {
    if (isMobile === false) {
      const card = carouselRef.current?.querySelector(".swiper-slide[data-year-index='" + index + "']");
      if (card) {
        card.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        setCurrentBulletIndex(index);
      }
    } else {
      setCurrentBulletIndex(index);
    }
  }

  return (
    <div
      className={classNames(
        isMobile && firstComponent && "hidden",
        "swiper-pagination-wrapper absolute md:mr-[20px] top-[2px] order-1 flex items-center w-[100%] overflow-auto no-scrollbar md:w-[136px] md:sticky md:justify-center md:order-1 md:top-[var(--header-height)] md:h-[calc(100vh-var(--header-height))]",
      )}
      style={{ top: `${scrollOffset}px` }}
    >
      <Swiper
        className={classNames(
          "flex items-center justify-center gap-m pagination-top swiper-pagination-clickable swiper-pagination-bullets h-full md:h-fit md:flex-col md:max-h-[100vh]",
          { "w-full": bulletType !== "Icons" },
          { "md:!ml-[40px] flex-1": bulletType === "Icons" },
        )}
        ref={paginationRef}
        slidesPerView={"auto"}
        spaceBetween={bulletType === "Icons" ? 0 : 32}
        centeredSlides={bulletType !== "Icons"}
        wrapperClass={cn(bulletType === "Icons" ? "gap-l justify-center" : "", "max-md:align-middle")}
        speed={800}
        onSwiper={onSwiper}
        onSlideChange={onSlideChange}
        onBreakpoint={onBreakpoint}
        breakpoints={{
          1024: {
            centeredSlides: false,
            direction: "vertical",
          },
        }}
      >
        {orderedFirstSlideIndex.map((item, index) => {
          const isActive = currentYearGroupIndex === index;
          return (
            <SwiperSlide
              key={item.title + "-" + index}
              className={classNames(
                "w-fit history-bullet font-primary text-s font-medium years h-auto text-white first:md:mt-xl",
                {
                  "bullet-active": isActive,
                },
                {
                  "min-w-3xl": bulletType !== "Icons",
                },
              )}
              onClick={() => {
                handleClick(index);
              }}
              data-index={item.firstIndex}
              tag="button"
              id={"history-bullet-" + index}
            >
              {bulletType === "Years" ? (
                <span className="scalable-wrapper">{item.year || ""}</span>
              ) : item.bulletIcon && isMobile ? (
                <>
                  <span className={cn(isActive && "glass-background")}></span>
                  <Icon className="stroke-white opacity-60 scalable-wrapper z-10" name={item.bulletIcon} size="24" />
                </>
              ) : (
                <span className="w-xs h-xs bg-white rounded-full block opacity-60 transition-all bullet-wrapper"></span>
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
      {bulletType === "Years" && (
        <span className={"active-page-border" + (currentYearGroupIndex >= 0 ? " visible" : "hidden")} ref={paginationActiveBorderRef}></span>
      )}
    </div>
  );
};
